import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";

// Eichgültigkeit nach MessEV Anlage 7 (Jahre); läuft zum Ende des Kalenderjahres ab
const EICH_YEARS = { cold_water: 6, hot_water: 5, heat_quantity: 5, district_heating: 5, gas: 8, electricity: 8, common_electricity: 8 };

export function calibrationInfo(m) {
  const c = m?.calibration_date;
  const n = EICH_YEARS[m?.meter_type];
  if (!c) return null;
  const label = c.length === 4 ? c : `${c.slice(8, 10)}.${c.slice(5, 7)}.${c.slice(0, 4)}`;
  if (!n) return { label, until: null, expired: false };
  const until = Number(c.slice(0, 4)) + n;
  return { label, until, expired: until < new Date().getFullYear(), soon: until === new Date().getFullYear() };
}

export function CalibrationField({ value, onChange }) {
  const yearOnly = !value || value.length === 4;
  const [mode, setMode] = useState(value && value.length > 4 ? "date" : "year");
  return (
    <div>
      <div className="flex items-center justify-between"><Label className="text-xs">Eichdatum</Label>
        <button type="button" className="text-[11px] text-teal-700 hover:underline" onClick={() => { setMode(mode === "year" ? "date" : "year"); onChange(""); }} data-testid="meter-calibration-mode">
          {mode === "year" ? "genaues Datum" : "nur Jahr"}</button></div>
      {mode === "year"
        ? <Input inputMode="numeric" maxLength={4} placeholder="z. B. 2024" value={yearOnly ? value || "" : value.slice(0, 4)} onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="meter-calibration-year" />
        : <DatePicker value={value && value.length > 4 ? value : ""} onChange={onChange} data-testid="meter-calibration-date" />}
    </div>
  );
}

export function UnitMultiSelect({ units, primary, value, onChange }) {
  const others = units.filter((u) => u.id !== primary);
  if (!primary || !others.length) return null;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="rounded-md border border-slate-200 p-2" data-testid="meter-extra-units">
      <div className="text-xs font-medium text-slate-700 mb-1">Zusätzlich zugeordnet (z. B. gemeinsamer Zähler zweier Einheiten)</div>
      <div className="max-h-32 overflow-y-auto grid grid-cols-2 gap-x-3">
        {others.map((u) => (
          <label key={u.id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer" data-testid={`meter-extra-unit-${u.id}`}>
            <Checkbox checked={value.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
            <span className="font-mono text-xs">{u.unit_number}</span><span className="text-xs text-slate-500 truncate">{u.location || ""}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export function MeterChangeDialog({ propertyId, meter, onDone }) {
  const d0 = new Date();
  const today = `${d0.getFullYear()}-${String(d0.getMonth() + 1).padStart(2, "0")}-${String(d0.getDate()).padStart(2, "0")}`;
  const [f, setF] = useState({ change_date: today, old_final_value: "", new_meter_number: "", new_start_value: "", new_calibration_date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const num = (v) => (v === "" ? null : Number(String(v).replace(",", ".")));
  const future = f.change_date > today;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/meters/${meter.id}/change`, {
        change_date: f.change_date, old_final_value: num(f.old_final_value), new_meter_number: f.new_meter_number.trim(),
        new_start_value: num(f.new_start_value), new_calibration_date: f.new_calibration_date || null, notes: f.notes || null });
      toast.success(data.status === "done" ? "Zählerwechsel gebucht – neuer Zähler ist aktiv" : `Zählerwechsel zum ${f.change_date.split("-").reverse().join(".")} geplant – Aufgabe angelegt`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Fehler beim Zählerwechsel"); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent className="max-w-lg" data-testid="meter-change-dialog">
      <DialogHeader><DialogTitle>Zählerwechsel</DialogTitle>
        <DialogDescription>Alter Zähler <span className="font-mono">{meter.meter_number}</span>. Datum heute oder früher = Wechsel wird sofort gebucht; späteres Datum = wird zum Datum automatisch umgestellt.</DialogDescription></DialogHeader>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Wechseldatum</Label><DatePicker value={f.change_date} onChange={(v) => set("change_date", v)} data-testid="meter-change-date" /></div>
        <div><Label className="text-xs">Endstand alter Zähler{future ? " (optional)" : ""}</Label><Input inputMode="decimal" value={f.old_final_value} onChange={(e) => set("old_final_value", e.target.value)} placeholder={meter.measurement_unit || ""} data-testid="meter-change-old-value" /></div>
        <div><Label className="text-xs">Neue Zählernummer</Label><Input value={f.new_meter_number} onChange={(e) => set("new_meter_number", e.target.value)} data-testid="meter-change-new-number" /></div>
        <div><Label className="text-xs">Anfangsstand neuer Zähler</Label><Input inputMode="decimal" value={f.new_start_value} onChange={(e) => set("new_start_value", e.target.value)} placeholder="z. B. 0" data-testid="meter-change-new-value" /></div>
        <div className="col-span-2"><CalibrationField value={f.new_calibration_date} onChange={(v) => set("new_calibration_date", v)} /></div>
        <div className="col-span-2"><Label className="text-xs">Notiz</Label><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="meter-change-notes" /></div>
      </div>
      {future && <div className="text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded px-2 py-1.5" data-testid="meter-change-future-hint">Geplanter Wechsel: Es wird eine Aufgabe zum Datum angelegt; der neue Zähler wird an diesem Tag automatisch aktiv.</div>}
      <DialogFooter><Button onClick={submit} disabled={busy || !f.new_meter_number.trim() || !f.change_date} className="bg-slate-900 hover:bg-slate-800" data-testid="meter-change-submit">
        {busy ? "Speichere…" : future ? "Wechsel planen" : "Wechsel buchen"}</Button></DialogFooter>
    </DialogContent>
  );
}
